/**
 * The development chat's conversations: shared, named, kept (#75).
 *
 * A thread belongs to the organisation, not to whoever started it. Two
 * bartenders developing the same drink read and add to the same thread, and
 * a manager can pick up where the closing shift left off — which is the
 * reason to keep conversations at all. A thread may be about one spec, the
 * drink being revised, or about none, a new drink being worked out.
 *
 * Messages are stored as the people read them: what was asked, by whom, and
 * what the chat answered, with the build it proposed. The model's tool calls
 * are not kept; each turn re-costs from the library as it stands, so a
 * stored number would only ever be a stale one.
 *
 * Storage lives in its own partition beside the org's (see `chatKeys`), so
 * the library load — which reads the org partition whole — does not grow
 * with every conversation the bar has.
 */
import { slugify } from "./library.js";
/** The longest title kept. Long enough for "Negroni with mezcal, lower ABV". */
export const MAX_CHAT_TITLE = 80;
/** How a thread is listed when nothing better can be said. */
export const UNTITLED_CHAT = "New chat";
/**
 * The title a thread starts with: its first question, tidied and cut at a
 * word, or the drink it is about when the question has no words.
 *
 * Named after the question rather than the drink because a bar revises its
 * Negroni more than once, and five threads called "Negroni" is a list nobody
 * can choose from.
 */
export function titleFrom(question, subjectName) {
    const text = question.replace(/\s+/g, " ").trim();
    if (!text)
        return subjectName?.trim() || UNTITLED_CHAT;
    const limit = 60;
    if (text.length <= limit)
        return text;
    const cut = text.slice(0, limit);
    const space = cut.lastIndexOf(" ");
    return `${(space > 30 ? cut.slice(0, space) : cut).replace(/[\s,.;:–—-]+$/, "")}…`;
}
/** A title a person typed, tidied, or an error. */
export function validateChatTitle(v) {
    if (typeof v !== "string")
        return { ok: false, error: "title must be a string" };
    const title = v.replace(/\s+/g, " ").trim();
    if (!title)
        return { ok: false, error: "title is empty" };
    if (title.length > MAX_CHAT_TITLE) {
        return { ok: false, error: `title is longer than ${MAX_CHAT_TITLE} characters` };
    }
    return { ok: true, value: title };
}
/** How many stored messages a turn sends the model. Twenty exchanges. */
export const CHAT_HISTORY_LIMIT = 40;
/**
 * The conversation as the model is sent it: the most recent `limit`
 * messages, user first, alternating.
 *
 * A thread can hold two questions in a row — a turn that failed before it
 * answered, or two people asking at once — and an answer with no text, from
 * a turn that only proposed a build. Neither is something to send as it is:
 * empty turns are dropped, and neighbours from the same side are joined so
 * the model reads both questions rather than being refused for the shape.
 */
export function modelHistory(messages, limit = CHAT_HISTORY_LIMIT) {
    const out = [];
    for (const m of messages) {
        const content = m.content.trim();
        if (!content)
            continue;
        const last = out[out.length - 1];
        if (last && last.role === m.role)
            last.content = `${last.content}\n\n${content}`;
        else
            out.push({ role: m.role, content });
    }
    let recent = out.slice(-limit);
    while (recent[0]?.role === "assistant")
        recent = recent.slice(1);
    return recent;
}
/** The newest build in a thread: what its working draft showed last. */
export function latestBuild(messages) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
        const build = messages[i]?.build;
        if (build)
            return build;
    }
    return null;
}
/**
 * Whether this person may delete a thread: whoever started it, or somebody
 * who may edit the recipe book. Anyone in the chat may read, continue and
 * rename a thread — it is the team's — but throwing away somebody else's
 * work is a manager's call.
 */
export function mayDeleteChat(thread, userId, canEditSpecs) {
    return thread.createdBy === userId || canEditSpecs;
}
export const DEFAULT_SHELF = "stock";
export function isShelfScope(v) {
    return v === "held" || v === "stock" || v === "everything" || v === "external";
}
/** How the composer's menu labels each one. */
export const SHELF_CHOICES = [
    { scope: "held", label: "Available now", hint: "Only what the last count says is behind the bar" },
    { scope: "stock", label: "Our stock", hint: "Anything on the ingredient list, counted or not" },
    { scope: "everything", label: "Everything", hint: "The list, plus anything else worth using" },
    { scope: "external", label: "External only", hint: "Deliberately off the list: what we would buy in" },
];
/**
 * What the model is told, as its own block of the system prompt.
 *
 * `counted` is whether a stock count is on file: without one, nothing knows
 * what is behind the bar, so "held" has nothing to stand on and says so
 * rather than inventing a shelf.
 */
export function shelfInstruction(scope, counted) {
    const head = "THE SHELF for this conversation, chosen by the team in the composer. Work to it and do not ask which it is:";
    switch (scope) {
        case "held":
            return counted
                ? `${head} only what BEHIND THE BAR says is held tonight. Do not build on anything that is not on that list, even if it is on the ingredient list. If what they want needs something that is not held, say so and offer the nearest thing that is.`
                : `${head} only what is held tonight — but no stock count is on file, so what is held is not known. Say that plainly, work to the ingredient list meanwhile, and suggest they finalise a count.`;
        case "stock":
            return `${head} the ingredient list, whether or not a count says it is held. Say when an idea needs something the bar is not holding tonight, but do not rule it out for that.`;
        case "everything":
            return `${head} the ingredient list plus anything else worth using. Reach past the list when the drink is better for it, add what is missing, and say plainly which lines are not on the list yet and that their cost is an estimate for a manager to confirm.`;
        case "external":
            return `${head} what the bar does not already carry. They are looking for what to buy in, so build around ingredients that are not on the ingredient list, name them plainly, and say what each would cost to bring in as an estimate for a manager to confirm. Use what is on the list only where a drink genuinely needs it alongside the new.`;
    }
}
/**
 * What the chat batched becomes a house-made ingredient (#180): its build is
 * the batched lines, kept per serve as a ratio, and the recipe pours it once
 * where the first batched line stood, at what those lines add up to. An
 * unbatched build is simply its lines.
 */
export function foldBatch(build) {
    const toLine = (l) => l.unit && l.unit !== "ml"
        ? { ingredientId: l.ingredientId, ml: l.ml, unit: l.unit, ...(l.qty !== undefined ? { qty: l.qty } : {}) }
        : { ingredientId: l.ingredientId, ml: l.ml, unit: "ml" };
    const batched = build.lines.filter((l) => l.batched);
    if (batched.length === 0)
        return { batch: null, lines: build.lines.map(toLine) };
    const name = (build.batchName?.trim() || `${build.name.trim()} Batch`).trim();
    const ingredientId = slugify(name);
    const pour = batched.reduce((sum, l) => sum + l.ml, 0);
    const lines = [];
    let placed = false;
    for (const l of build.lines) {
        if (!l.batched)
            lines.push(toLine(l));
        else if (!placed) {
            lines.push({ ingredientId, ml: +pour.toFixed(2), unit: "ml" });
            placed = true;
        }
    }
    return { batch: { ingredientId, name, build: { lines: batched.map(toLine), yieldMl: null, shelfLifeDays: null } }, lines };
}
//# sourceMappingURL=chat.js.map